// Pure rate-card engine — faithful port of the logic in Merchant_Onboarding_v6.html.
// All functions are side-effect free: they read a plain `wizard` state object
// (the pricingWizard slice state) and RETURN computed data. The slice/components
// decide what to store.

import { SERVICE_TYPES, normalizeStCode, ratesForParent } from '../constants';

// Converts live API groups to [parent, account_code, service_type, payment_mode] tuples.
// `selection` (wizard.accountCodeSelection) narrows the codes per service type:
// an absent entry for a group's code means "all active codes" (the default).
function getEffectiveParentMap(groups, selection) {
  const map = [];
  (groups || []).forEach((group) => {
    const sel = selection && selection[group.code];
    const selSet = sel ? new Set(sel) : null;
    (group.linkedCodes || []).filter((c) => c.isActive !== false).forEach((lc) => {
      if (selSet && !selSet.has(lc.cpAccountCode)) return;
      map.push([lc.parent || lc.cpAccountCode, lc.cpAccountCode, group.code, 'BOTH']);
    });
  });
  return map;
}

// Account codes selected for a service type. Absent selection => all active codes
// (backward-compatible default). Used by the pricing-upload template builder.
export function selectedCodesForService(selection, serviceId, allActiveCodes) {
  const sel = selection && selection[serviceId];
  if (!sel) return allActiveCodes;
  const set = new Set(sel);
  return (allActiveCodes || []).filter((c) => set.has(c));
}

// ── Canonical slab structures ───────────────────────────────────────────────
export const STANDARD_SLABS_50KG = (() => {
  const arr = [];
  for (let g = 0; g < 10000; g += 500) arr.push([g, g + 500]); // 20 slabs
  for (let g = 10000; g < 50000; g += 1000) arr.push([g, g + 1000]); // 40 slabs
  return arr;
})();
export const STANDARD_SLAB_KEYS_50KG = STANDARD_SLABS_50KG.map(([a, b]) => `${a}-${b}`);
export const EXTRACTED_SLAB_KEYS = [...STANDARD_SLAB_KEYS_50KG];

const SLAB_TEMPLATES_LEGACY_5KG = [
  { id: '250-250-500', label: '250 + 250 + 500 GMS (legacy · 5 kg max)', slabs: [[0, 250], [250, 500], [500, 1000], [1000, 1500], [1500, 2000], [2000, 2500], [2500, 3000], [3000, 3500], [3500, 4000], [4000, 4500], [4500, 5000]] },
  { id: '250-750-500', label: '250 + 750 + 500 GMS (legacy · 2.5 kg max)', slabs: [[0, 250], [250, 1000], [1000, 1500], [1500, 2000], [2000, 2500]] },
  { id: '500-500-500', label: '500 + 500 + 500 GMS (legacy · 5 kg max)', slabs: [[0, 500], [500, 1000], [1000, 1500], [1500, 2000], [2000, 2500], [2500, 3000], [3000, 3500], [3500, 4000], [4000, 4500], [4500, 5000]] },
  { id: '750-250-500', label: '750 + 250 + 500 GMS (legacy · 2.5 kg max)', slabs: [[0, 750], [750, 1000], [1000, 1500], [1500, 2000], [2000, 2500]] },
  { id: '750-500-500', label: '750 + 500 + 500 GMS (legacy · 2.75 kg max)', slabs: [[0, 750], [750, 1250], [1250, 1750], [1750, 2250], [2250, 2750]] },
];

export const SLAB_TEMPLATES = [
  { id: 'proship-50kg', label: 'ProShip canonical · 60 slabs (20×500 g + 40×1000 g · 50 kg max)', slabs: STANDARD_SLABS_50KG },
  ...SLAB_TEMPLATES_LEGACY_5KG,
  { id: 'custom', label: 'Custom slabs', slabs: null },
];

export const ZONES = [
  { id: 1, name: 'Local' }, { id: 2, name: 'Regional' }, { id: 3, name: 'Metro-to-Metro' }, { id: 4, name: 'ROI' }, { id: 5, name: 'Special' },
];
export const ZONE_DELTA = { 1: 0, 2: 8, 3: 14, 4: 20, 5: 32 };


// ── Lane classification ─────────────────────────────────────────────────────
// v6 lanes for a Blended rate card: Surface · Air · SDD/NDD (+ reverse → RVP).
// SDD and NDD (same-city / inter-city next-day) form their own commercial lane.
export function classifyServiceLane(svcId) {
  const s = String(svcId || '').toUpperCase();
  if (!s) return 'surface';
  if (s.startsWith('REV_')) return 'reverse';
  if (s.startsWith('SDD_') || s.includes('NDD')) return 'sdd_ndd';
  if (s.includes('AIR')) return 'air';
  if (s === 'H2TO4' || s === 'SUB2H') return 'air';
  if (s.includes('SURFACE')) return 'surface';
  return 'surface';
}
export function classifyParentLane(parent) {
  const p = String(parent || '').toUpperCase();
  if (p.includes('REVERSE')) return 'reverse';
  if (p.includes('SDD') || p.includes('NDD')) return 'sdd_ndd';
  if (p.includes('AIR')) return 'air';
  if (p.includes('SURFACE')) return 'surface';
  return 'surface';
}

// Display metadata per blended lane.
export const BLENDED_LANES = [
  { id: 'surface', label: 'Surface', icon: '🚛', tabKey: '__blended_surface__', tcsKey: 'tcs_blended_surface', tcKey: 'blended_surface' },
  { id: 'air', label: 'Air', icon: '✈️', tabKey: '__blended_air__', tcsKey: 'tcs_blended_air', tcKey: 'blended_air' },
  { id: 'sdd_ndd', label: 'SDD / NDD', icon: '🚀', tabKey: '__blended_sdd_ndd__', tcsKey: 'tcs_blended_sdd_ndd', tcKey: 'blended_sdd_ndd' },
];

export function getBlendedLaneStats(wizard) {
  const lanes = {
    surface: { parents: [], accounts: [] },
    air: { parents: [], accounts: [] },
    sdd_ndd: { parents: [], accounts: [] },
  };
  const resolvedIds = new Set((wizard.resolvedServices || []).filter((s) => s && s.id && !s.id.startsWith('REV_')).map((s) => s.id));
  if (!resolvedIds.size) return lanes;
  getEffectiveParentMap(wizard.serviceabilityGroups, wizard.accountCodeSelection).forEach(([par, acc, svcId]) => {
    if (!resolvedIds.has(svcId)) return;
    const lane = classifyServiceLane(svcId);
    if (!lanes[lane]) return; // reverse → RVP step, not a blended forward lane
    if (!lanes[lane].parents.includes(par)) lanes[lane].parents.push(par);
    if (!lanes[lane].accounts.includes(acc)) lanes[lane].accounts.push(acc);
  });
  return lanes;
}

// Forward blended lanes that actually have at least one selected account code —
// this is the sheet list for a Blended rate-card template (one sheet per lane,
// parents collapsed). Reverse is excluded (handled by the RVP step).
export function blendedForwardLanes(wizard) {
  const stats = getBlendedLaneStats(wizard);
  return BLENDED_LANES.filter((l) => (stats[l.id] && stats[l.id].accounts.length > 0));
}

// Every (parent, account_code, service) tuple selected under a blended lane.
// Used at upload time to fan one lane's prices onto every courier account code.
export function blendedLaneAssignments(wizard, lane) {
  const resolvedIds = new Set(
    (wizard.resolvedServices || [])
      .filter((s) => s && s.id && !String(s.id).toUpperCase().startsWith('REV_'))
      .map((s) => s.id),
  );
  const out = [];
  const seen = new Set();
  getEffectiveParentMap(wizard.serviceabilityGroups, wizard.accountCodeSelection).forEach(([parent, account, svcId]) => {
    if (!resolvedIds.has(svcId)) return;
    if (classifyServiceLane(svcId) !== lane) return;
    const key = `${account}|${svcId}`;
    if (seen.has(key)) return;
    seen.add(key);
    out.push({ parent, account_code: account, service: svcId });
  });
  return out;
}

export function blendedSourceForLane(wizard, lane) {
  const inScope = (getBlendedLaneStats(wizard)[lane]?.parents || [])[0];
  if (inScope) return inScope;
  const ext = wizard.agreement && wizard.agreement.extracted;
  if (!ext || !ext.rates) return null;
  return Object.keys(ext.rates).find((p) => classifyParentLane(p) === lane) || null;
}


// ── Slabs for the chosen template ───────────────────────────────────────────
export function slabsForTemplate(wizard) {
  const tpl = SLAB_TEMPLATES.find((t) => t.id === wizard.rateCard.slabTemplate) || SLAB_TEMPLATES[0];
  if (wizard.rateCard.slabTemplate === 'custom') {
    const parts = (wizard.rateCard.customSlabs || '').split(',').map((s) => +s.trim()).filter(Boolean);
    let acc = 0; const slabs = [];
    parts.forEach((p) => { slabs.push([acc, acc + p]); acc += p; });
    return slabs.length ? slabs : STANDARD_SLABS_50KG;
  }
  return tpl.slabs || STANDARD_SLABS_50KG;
}

// ── RVP math ────────────────────────────────────────────────────────────────
// Mode A RVP nQC from the per-courier formula — calculated on the FORWARD price `fwd`,
// per the selected type:
//   add/flat            → FWD + ₹X
//   mult                → FWD × M
//   higher_of (default) → max(FWD + ₹X, FWD × M)
export function computeFormulaPreview(f, fwd) {
  fwd = Number(fwd) || 0;
  if (!f) return fwd.toFixed(2);
  const flat = Number(f.flat) || 0;
  const mult = Number(f.mult) || 0;
  if (f.type === 'mult') return (fwd * mult).toFixed(2);
  if (f.type === 'flat' || f.type === 'add') return (fwd + flat).toFixed(2);
  return Math.max(fwd + flat, fwd * mult).toFixed(2);
}

// Mode A RVP { nQC, QC } from the formula applied to the forward price:
// nQC = formula(fwd), QC = nQC + formula.qc.
export function computeModeARvp(f, fwd) {
  const noqc = +Number(computeFormulaPreview(f, fwd)).toFixed(2);
  const qc = +(noqc + (Number(f && f.qc) || 0)).toFixed(2);
  return { rvp_noqc: noqc, rvp_qc: qc };
}
export function computeRvp(blended, price) {
  const flat = Number(blended.rvp_flat) || 0;
  const pct = Number(blended.rvp_pct) || 0;
  const qc = Number(blended.qc) || 0;
  let noqc;
  if (blended.rvp_formula === 'pct_fwd') noqc = (price * pct) / 100;
  else if (blended.rvp_formula === 'higher_of') noqc = Math.max(flat + price, (price * pct) / 100);
  else noqc = flat + price;
  noqc = +noqc.toFixed(2);
  return { rvp_noqc: noqc, rvp_qc: +(noqc + qc).toFixed(2) };
}

// ── T&C resolution per parent (override → lane → blended) ───────────────────
export function resolveTcForParent(wizard, parent, svcId) {
  const isBlendedMode = wizard.rateCardMode === 'blended';
  const tc = wizard.tc.blended || {};
  const ov = isBlendedMode ? {} : ((wizard.tc.perCourier && wizard.tc.perCourier[parent]) || {});
  let lane = null; let laneName = null;
  if (isBlendedMode) {
    const cls = svcId ? classifyServiceLane(svcId) : classifyParentLane(parent);
    if (cls === 'air') { lane = wizard.tc.blended_air || {}; laneName = 'air'; }
    else if (cls === 'sdd_ndd') { lane = wizard.tc.blended_sdd_ndd || {}; laneName = 'sdd_ndd'; }
    else if (cls === 'surface') { lane = wizard.tc.blended_surface || {}; laneName = 'surface'; }
  }
  const pick = (k, fallback) => {
    if (ov[k] != null && ov[k] !== '') return ov[k];
    if (lane && lane[k] != null && lane[k] !== '') return lane[k];
    if (tc[k] != null && tc[k] !== '') return tc[k];
    return fallback;
  };
  return {
    cod_fixed: Number(pick('cod_fixed', 0)) || 0,
    cod_pct: Number(pick('cod_pct', 0)) || 0,
    cod_op: pick('cod_op', 'MAX'),
    rto_pct: Number(pick('rto_pct', 0)) || 0,
    fsc: Number(pick('fsc', 0)) || 0,
    rvp_formula: pick('rvp_formula', 'flat_plus_fwd'),
    rvp_flat: Number(pick('rvp_flat', 0)) || 0,
    rvp_pct: Number(pick('rvp_pct', 0)) || 0,
    qc: Number(pick('qc', 0)) || 0,
    remit: pick('remit', ''),
    has_override: !!Object.keys(ov).length,
    lane: laneName,
  };
}

export function reverseParentsInScope(wizard) {
  const reverseSvcIds = new Set((wizard.resolvedServices || []).filter((s) => s.id && s.id.startsWith('REV_')).map((s) => s.id));
  if (!reverseSvcIds.size) return [];
  const extractionTabs = wizard.agreement.extracted?.courierTabs;
  const parents = new Set();
  if (extractionTabs?.length) {
    // All confirmed couriers are potential RVP parents; dedup by courierName
    const seenCouriers = new Set();
    extractionTabs.forEach((tab) => {
      if (!seenCouriers.has(tab.courierName)) {
        seenCouriers.add(tab.courierName);
        parents.add(tab.courierName);
      }
    });
  } else {
    getEffectiveParentMap(wizard.serviceabilityGroups, wizard.accountCodeSelection).forEach((r) => {
      if (reverseSvcIds.has(r[2])) parents.add(r[0]);
    });
  }
  return [...parents].sort();
}

// Reverse account codes (with parent) in scope — honours the Profile selection.
// Mode B reverse rate card is per account code, like the forward courier-wise card.
export function reverseAccountCodesInScope(wizard) {
  const reverseSvcIds = new Set((wizard.resolvedServices || [])
    .filter((s) => s.id && s.id.startsWith('REV_')).map((s) => s.id));
  if (!reverseSvcIds.size) return [];
  const out = [];
  const seen = new Set();
  (wizard.serviceabilityGroups || []).forEach((g) => {
    if (!reverseSvcIds.has(g.code)) return;
    const sel = wizard.accountCodeSelection && wizard.accountCodeSelection[g.code];
    const selSet = sel ? new Set(sel) : null;
    (g.linkedCodes || []).filter((lc) => lc.isActive !== false).forEach((lc) => {
      if (selSet && !selSet.has(lc.cpAccountCode)) return;
      if (seen.has(lc.cpAccountCode)) return;
      seen.add(lc.cpAccountCode);
      out.push({ accountCode: lc.cpAccountCode, parent: lc.parent || lc.cpAccountCode });
    });
  });
  return out;
}

// ── The big one: build every rate-card row ──────────────────────────────────
export function buildRateCardRows(wizard) {
  const ext = wizard.agreement.extracted?.rates ? wizard.agreement.extracted : null;
  const slabs = slabsForTemplate(wizard);
  const rows = [];
  const isBlended = wizard.rateCardMode === 'blended';

  // Build a flat list of [parent, accountCode] pairs from confirmed courierTabs.
  // Deduplicates by courierName so a courier appearing in multiple extraction service
  // types is still represented once. This list is used for EVERY resolvedService —
  // not filtered by extraction serviceType — so couriers like Bluedart or DTDC that
  // only appear in one extraction serviceType are still visible across all rate card tabs.
  const extractionTabs = wizard.agreement.extracted?.courierTabs;
  let allExtractionPairs = null; // [[parent, acc], ...] when extraction exists
  if (extractionTabs?.length) {
    const seenCouriers = new Set();
    allExtractionPairs = [];
    extractionTabs.forEach((tab) => {
      if (seenCouriers.has(tab.courierName)) return; // deduplicate by courier name
      seenCouriers.add(tab.courierName);
      const needle = tab.courierName.toLowerCase();
      const codes = new Set();
      (wizard.serviceabilityGroups || []).forEach((g) => {
        (g.linkedCodes || []).filter((lc) => lc.isActive !== false).forEach((lc) => {
          const p = (lc.parent || lc.cpAccountCode || '').toLowerCase();
          if (p.includes(needle) || needle.includes(p)) codes.add(lc.cpAccountCode);
        });
      });
      // No match in serviceabilityGroups — use courierName as placeholder account code
      (codes.size > 0 ? [...codes] : [tab.courierName]).forEach((acc) => {
        allExtractionPairs.push([tab.courierName, acc]);
      });
    });
  }

  const blendedSurfaceSource = isBlended ? (ext?.rates?.[blendedSourceForLane(wizard, 'surface')] || null) : null;
  const blendedAirSource = isBlended ? (ext?.rates?.[blendedSourceForLane(wizard, 'air')] || null) : null;
  const blendedSddNddSource = isBlended ? (ext?.rates?.[blendedSourceForLane(wizard, 'sdd_ndd')] || null) : null;

  const rvpModeA = wizard.rvp.enabled && wizard.rvp.mode === 'forward_linked';
  const rvpBlendedGroup = wizard.rateCardMode === 'blended';

  function rvpFormulaFor(revParent) {
    // Per-courier override if set, else the shared __blended__ formula (the one edited
    // in Commercials), else a sensible default.
    const fm = wizard.rvp.formulas || {};
    const f = rvpBlendedGroup ? fm.__blended__ : (fm[revParent] || fm.__blended__);
    return f || { type: 'flat', flat: 25, mult: 1.3 };
  }

  (wizard.resolvedServices || []).forEach((s) => {
    const isRevSvc = s.family === 'Reverse' || (s.id && s.id.startsWith('REV_'));
    // Accounts actually linked to THIS service in serviceabilityGroups (with their parent).
    const svcLinked = (wizard.serviceabilityGroups || [])
      .filter((g) => normalizeStCode(g.code) === normalizeStCode(s.id))
      .flatMap((g) => (g.linkedCodes || []).filter((lc) => lc.isActive !== false)
        .map((lc) => [lc.parent || lc.cpAccountCode, lc.cpAccountCode]));
    // RVP Mode A (forward-linked): reverse services are priced on their OWN reverse
    // couriers, with RVP derived from the forward sibling (see isRev branch below). So
    // build their rows from the reverse codes selected in Profile — not the forward
    // extraction couriers, which would hide every reverse code in Mode A. Other modes
    // keep the legacy behaviour (every service uses the shared extraction courier set).
    const useReversePairs = isRevSvc && rvpModeA && allExtractionPairs?.length && svcLinked.length;
    // Only build rows when a pricing file has been uploaded / extraction confirmed.
    // Return nothing when no extraction data — rate card shows empty until a file is provided.
    const mapped = useReversePairs ? svcLinked : (allExtractionPairs ?? []);
    if (!mapped.length) return;
    const ar = wizard.arrangement[s.id] || { type: 'ZWZ', mode: 'cp' };
    const svcLane = classifyServiceLane(s.id);
    // Honour the per-service account-code selection from the Profile step. Codes
    // de-selected there are excluded; placeholder accounts (couriers absent from
    // serviceabilityGroups) are never in the active set, so they always pass.
    const activeForSvc = svcLinked.map(([, acc]) => acc);
    const servedForSvc = new Set(activeForSvc);
    const sel = wizard.accountCodeSelection && wizard.accountCodeSelection[s.id];
    let excludedCodes = null;
    if (sel) {
      const selSet = new Set(sel);
      excludedCodes = new Set(activeForSvc.filter((c) => !selSet.has(c)));
    }
    mapped.forEach(([par, acc]) => {
      if (excludedCodes && excludedCodes.has(acc)) return;
      // Blended: a courier only gets rows for services it actually serves, so it stays
      // in its own lane (Air courier → Air tab only). Without this every courier was
      // built under every service, collapsing all lanes into one and showing mixed
      // prices. Courier-wise keeps its "visible on all tabs" behaviour unchanged.
      if (isBlended && servedForSvc.size && !servedForSvc.has(acc)) return;
      const tcr = resolveTcForParent(wizard, par, s.id);
      let parentRates;
      if (isBlended) {
        // Blended = ONE rate per lane for all couriers. Resolve each lane to its own
        // representative source; never fall back across lanes (Air/SDD must not borrow
        // Surface — that leaked the Surface price onto every lane). Fall back to the
        // parent's own rates only if the lane source is missing.
        if (svcLane === 'air') parentRates = blendedAirSource || ratesForParent(ext?.rates, par);
        else if (svcLane === 'sdd_ndd') parentRates = blendedSddNddSource || ratesForParent(ext?.rates, par);
        else if (svcLane === 'surface') parentRates = blendedSurfaceSource || ratesForParent(ext?.rates, par);
        else parentRates = ratesForParent(ext?.rates, par);
      } else {
        parentRates = ratesForParent(ext?.rates, par);
      }
      ZONES.forEach((z) => {
        slabs.forEach((slab, i) => {
          let price = null;
          if (parentRates && parentRates[z.id]) {
            const exactKey = `${slab[0]}-${slab[1]}`;
            if (parentRates[z.id][exactKey] !== undefined) price = parentRates[z.id][exactKey] || null;
            else {
              const sk = ext?.slabKeys?.[Math.min(i, (ext.slabKeys.length || 1) - 1)];
              price = sk != null ? (parentRates[z.id][sk] ?? null) : null;
            }
          }
          const isRev = s.family === 'Reverse';
          let rvp;
          if (rvpModeA) {
            // Mode A: RVP = max(FWD + Flat, FWD × Multiplier) from the per-courier
            // formula on the forward price; QC = nQC + the formula's QC charge.
            // (foldIndependentRvpIntoRows re-applies this per-row at preview/publish.)
            rvp = computeModeARvp(rvpFormulaFor(par), price);
          } else if (isRev) {
            rvp = { rvp_noqc: price, rvp_qc: +(price + tcr.qc).toFixed(2) };
          } else {
            rvp = computeRvp({ rvp_formula: tcr.rvp_formula, rvp_flat: tcr.rvp_flat, rvp_pct: tcr.rvp_pct, qc: tcr.qc }, price);
          }
          rows.push({
            account_code: acc, parent: par, service: s.id, arrangement: ar.type, mode: ar.mode,
            zone: z.id, start: slab[0], min: slab[0], max: slab[1],
            price, addl_unit: 0, addl_rate: 0, vc: wizard.agreement.vc, tax: 18, fsc: tcr.fsc, docket: 0,
            cod_pct: tcr.cod_pct, cod_op: tcr.cod_op, cod_fixed: tcr.cod_fixed,
            cod_charge_type: 'FIXED',
            rvp_noqc: rvp.rvp_noqc, rvp_qc: rvp.rvp_qc, rto_pct: tcr.rto_pct,
            tc_source: isBlended ? `blended · ${tcr.lane === 'air' ? 'Air' : tcr.lane === 'sdd_ndd' ? 'SDD / NDD' : 'Surface'}` : (tcr.has_override ? `${par} (override)` : 'blended default'),
          });
        });
      });
    });
  });

  if (wizard.agreement.gst_stance === 'inclusive') {
    rows.forEach((r) => {
      r.price_incl = r.price; r.rvp_noqc_incl = r.rvp_noqc; r.rvp_qc_incl = r.rvp_qc;
      r.price = +(r.price / 1.18).toFixed(2);
      r.rvp_noqc = +(r.rvp_noqc / 1.18).toFixed(2);
      r.rvp_qc = +(r.rvp_qc / 1.18).toFixed(2);
    });
  }
  return rows;
}

// ── RVP Mode B (independent) slab patterns + seed ───────────────────────────
export const RVP_SLAB_PATTERNS = [
  { id: 'p1', label: 'Pattern 1 · Up to 500 g + incremental 500 g', slabs: (() => { const a = [[0, 500]]; for (let g = 500; g < 10000; g += 500) a.push([g, g + 500]); return a; })() },
  { id: 'p2', label: 'Pattern 2 · Up to 750 g + 250 g + incremental 500 g', slabs: (() => { const a = [[0, 750], [750, 1000]]; for (let g = 1000; g < 10000; g += 500) a.push([g, g + 500]); return a; })() },
  { id: 'p3', label: 'Custom (use forward slab template)', slabs: null },
];
export const RVP_ZONES = [
  { id: 1, name: 'Local' }, { id: 2, name: 'Within State' }, { id: 3, name: 'Metro' }, { id: 4, name: 'ROI' }, { id: 5, name: 'Special' },
];

// Seeds only the STRUCTURE (parents × zones × slabs) with blank cells — never
// fabricated prices. The grid renders empty until the user types or uploads a
// template, and foldIndependentRvpIntoRows skips null cells, so nothing fake is
// ever folded onto the forward rows / sent to the backend.
export function seedRvpExtracted(wizard) {
  const parents = reverseParentsInScope(wizard);
  const slabs = (RVP_SLAB_PATTERNS.find((p) => p.id === wizard.rvp.slabPattern) || RVP_SLAB_PATTERNS[0]).slabs || STANDARD_SLABS_50KG;
  const rates = {};
  parents.forEach((p) => {
    rates[p] = {};
    RVP_ZONES.forEach((z) => {
      rates[p][z.id] = {};
      slabs.forEach(([min, max]) => {
        rates[p][z.id][`${min}-${max}`] = null;
      });
    });
  });
  return { rates, slabKeys: slabs.map(([a, b]) => `${a}-${b}`), source: 'empty — enter or upload rates' };
}

// ── RVP Mode B fold — overlay the independent RVP matrix onto forward rows ────
// The backend has no separate RVP rate card: RVP rides on each forward row as
// rvp_qc / rvp_noqc (same account_code, zone, weight slab). When RVP is
// independent (Mode B) the editable matrix (rvp.extracted.rates:
// parent → zone(int) → "min-max") holds the no-QC base price; QC adds the
// parent/global QC fee. This writes those onto every matching forward row so the
// existing save-rate-card payload carries the independent RVP rates.
// Mode A (forward-linked) rows are overlaid with the LIVE per-courier formula here
// (not left to whatever was baked at upload), so preview + publish always match the
// current formula even if it was edited after the sheet was uploaded.
export function foldIndependentRvpIntoRows(rows, wizard) {
  const rvp = (wizard && wizard.rvp) || {};
  // Mode A: RVP = formula(Flat, M) + QC charges. Recompute from the current formula.
  if (rvp.enabled && rvp.mode === 'forward_linked') {
    const fm = rvp.formulas || {};
    const inclusive = !!(wizard.agreement && wizard.agreement.gst_stance === 'inclusive');
    const exDiv = (v) => (v == null ? v : +(v / 1.18).toFixed(2));
    return rows.map((r) => {
      const f = (wizard.rateCardMode === 'blended' ? fm.__blended__ : (fm[r.parent] || fm.__blended__)) || {};
      // Forward price for the formula. For inclusive GST the stored price is ex-GST, so
      // gross it up first, run the formula, then divide the RVP back down.
      const fwd = inclusive ? (Number(r.price_incl != null ? r.price_incl : r.price) || 0) : (Number(r.price) || 0);
      const m = computeModeARvp(f, fwd);
      return inclusive
        ? { ...r, rvp_noqc: exDiv(m.rvp_noqc), rvp_qc: exDiv(m.rvp_qc), rvp_noqc_incl: m.rvp_noqc, rvp_qc_incl: m.rvp_qc }
        : { ...r, rvp_noqc: m.rvp_noqc, rvp_qc: m.rvp_qc };
    });
  }
  const isIndependent = rvp.enabled && rvp.mode === 'independent' && rvp.extracted;
  if (!isIndependent) return rows;
  const qcFee = (rvp.tcs && rvp.tcs.qc_applicable && Number(rvp.tcs.qc_amount)) || 0;

  // Preferred source: the full reverse rows (Mode B matrix carries its own
  // rvp_noqc / rvp_qc per parent × zone × slab). Index them for O(1) lookup.
  const reverseRows = Array.isArray(rvp.extracted.rows) ? rvp.extracted.rows : null;
  if (reverseRows && reverseRows.length) {
    const idx = {};
    reverseRows.forEach((rr) => {
      const k = `${rr.parent}|${rr.zone}|${rr.min}-${rr.max}`;
      idx[k] = rr;
    });
    return rows.map((r) => {
      const rr = idx[`${r.parent}|${r.zone}|${r.min}-${r.max}`];
      if (!rr) return r;
      const base = rr.rvp_noqc != null ? Number(rr.rvp_noqc) : null;
      const qc = rr.rvp_qc != null
        ? Number(rr.rvp_qc)
        : (base != null ? +(base + qcFee).toFixed(2) : null);
      return {
        ...r,
        ...(base != null ? { rvp_noqc: base } : {}),
        ...(qc != null ? { rvp_qc: qc } : {}),
      };
    });
  }

  // Fallback: legacy single-value / { noqc, qc } rate map.
  const matrix = rvp.extracted.rates;
  if (!matrix) return rows;
  return rows.map((r) => {
    const byZone = matrix[r.parent] && matrix[r.parent][r.zone];
    const cell = byZone && byZone[`${r.min}-${r.max}`];
    if (cell == null) return r;
    // A cell is either { noqc, qc } (Mode B two-column matrix) or a bare number
    // (legacy / uploaded = nQC base). nQC is the base; QC uses the entered value,
    // else falls back to nQC + the global QC fee from RVP T&Cs.
    const noqc = typeof cell === 'object' ? cell.noqc : cell;
    const qcEntered = typeof cell === 'object' ? cell.qc : null;
    if (noqc == null && qcEntered == null) return r;
    const base = noqc != null ? Number(noqc) : null;
    const qc = qcEntered != null
      ? Number(qcEntered)
      : (base != null ? +(base + qcFee).toFixed(2) : null);
    return {
      ...r,
      ...(base != null ? { rvp_noqc: base } : {}),
      ...(qc != null ? { rvp_qc: qc } : {}),
    };
  });
}


// ── COD display string ──────────────────────────────────────────────────────
export function refreshCodDef(target) {
  const op = target.cod_op || 'MAX';
  const fx = Number(target.cod_fixed) || 0;
  const pc = Number(target.cod_pct) || 0;
  if (fx > 0 && pc > 0) return `${op}(₹${fx} fixed, ${pc}% of COD value)`;
  if (fx > 0) return `₹${fx} flat`;
  if (pc > 0) return `${pc}% of COD value`;
  return '—';
}

// ── Date helpers ────────────────────────────────────────────────────────────
export function todayISO() { return new Date().toISOString().slice(0, 10); }
export function tomorrowISO() { const d = new Date(); d.setDate(d.getDate() + 1); return d.toISOString().slice(0, 10); }
export function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  return d.toLocaleDateString(undefined, { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' });
}
export function daysFromToday(iso) {
  if (!iso) return '';
  const d = new Date(iso + 'T00:00:00'); const t = new Date(todayISO() + 'T00:00:00');
  const n = Math.round((d - t) / 86400000);
  if (n === 0) return 'today';
  if (n === 1) return 'tomorrow';
  return `in ${n} days`;
}

// Group consecutive equal-width slabs for the summary chip.
export function summarizeSlabGroups(slabs) {
  const groups = [];
  slabs.forEach(([a, b]) => {
    const width = b - a;
    const last = groups[groups.length - 1];
    if (last && last.width === width) { last.count += 1; last.max = b; }
    else groups.push({ width, count: 1, min: a, max: b });
  });
  return groups;
}

export const SERVICE_BY_ID = Object.fromEntries(SERVICE_TYPES.map((s) => [s.id, s]));
